import {
  FileOptionsResponse,
  Jurisdiction,
  ModesAndModels,
  ModesAndModelsResponse,
  ThemeTree,
  ThemesResponse,
} from '../../../types/chatSettings';
import { baseApi } from 'store/api/base-api';
import { transformThemeTreeResponse } from './utils';
import { ModelKit, User } from 'types/user';
import { userApi } from 'store/features/user/user-api';
import { transformExpertTemplate } from 'store/features/user/utils/transformUserResponse';

export const chatSettingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getThemes: builder.query<ThemesResponse, void>({
      query: () => ({
        // url: `/api/augmented/theme-tree?themeId=null`,
        url: '/api/augmented/themes',
        method: 'GET',
      }),
    }),
    getJurisdictions: builder.query<Jurisdiction[], void>({
      query: () => ({
        url: '/api/jurisdictions',
        method: 'GET',
      }),
    }),
    getModesAndModels: builder.query<ModesAndModels, void>({
      query: () => ({
        url: '/api/modes',
        method: 'GET',
      }),
      transformResponse: (response: ModesAndModelsResponse): ModesAndModels => {
        const modelIcons = response.providerFamilies.reduce<
          ModesAndModels['modelIcons']
        >((acc, family) => {
          acc[family.id] = {
            name: family.name,
            iconSvg: family.iconSvg,
          };
          return acc;
        }, {});

        return {
          modes: response.expertise,
          models: response.modes,
          modelIcons,
        };
      },
    }),
    getThemeTree: builder.query<ThemeTree, string>({
      query: (themeId) => ({
        url: `/api/augmented/theme-tree?themeId=${themeId}`,
        method: 'GET',
      }),
      transformResponse: transformThemeTreeResponse,
    }),
    getFileOptions: builder.query<FileOptionsResponse, void>({
      query: () => ({
        url: '/api/files/options',
        method: 'GET',
      }),
    }),

    saveModelKit: builder.mutation<ModelKit, Omit<ModelKit, 'id' | 'canApply'>>(
      {
        query: ({ models, mainModel }) => ({
          url: '/api/chat/expert-templates',
          method: 'POST',
          body: { expertModes: models, summaryMode: mainModel },
        }),
        transformResponse: transformExpertTemplate,
        async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
          try {
            const { data: savedKit } = await queryFulfilled;

            dispatch(
              userApi.util.updateQueryData(
                'getUserData',
                undefined,
                (draft: User) => {
                  draft.modelsKit.push(savedKit);
                },
              ),
            );
          } catch {
            // ошибка отображается через состояние мутации в компоненте
          }
        },
      },
    ),
    deleteModelKit: builder.mutation<void, { modelKitId: string }>({
      query: ({ modelKitId }) => ({
        url: `/api/chat/expert-templates/${modelKitId}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ modelKitId }, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;

          dispatch(
            userApi.util.updateQueryData(
              'getUserData',
              undefined,
              (draft: User) => {
                draft.modelsKit = draft.modelsKit.filter(
                  (kit) => kit.id !== modelKitId,
                );
              },
            ),
          );
        } catch {
          // ошибка отображается через состояние мутации в компоненте
        }
      },
    }),
  }),
});

export const {
  useGetThemesQuery,
  useGetJurisdictionsQuery,
  useGetModesAndModelsQuery,
  useGetThemeTreeQuery,
  useGetFileOptionsQuery,
  useSaveModelKitMutation,
  useDeleteModelKitMutation,
} = chatSettingsApi;
